:where(button, a) {
  all: unset;
  display: inline-flex;
  align-items: center;
  user-select: none;
  touch-action: manipulation;
  cursor: pointer;
}

button:disabled {
  pointer-events: none;
}

*,
::after,
::before {
  box-sizing: border-box;
}

:host {
  --ch-tab-transition-duration: 150ms;
  /**
   * @prop --ch-tab-button__image-size:
   * Specifies the box size that contains the start images of the tab buttons.
   * @default 0.875em
   */
  --ch-tab-button__image-size: 0.875em;
  /**
   * @prop --ch-tab-button__background-image-size:
   * Specifies the size of the start images of the tab buttons.
   * @default 100%
   */
  --ch-tab-button__background-image-size: 100%;
  /**
   * @prop --ch-tab-close-button__image-size:
   * Specifies the box size that contains an image for the close button.
   * @default 0.875em
   */
  --ch-tab-close-button__image-size: 0.875em;
  /**
    * @prop --ch-tab-close-button__background-image-size:
    * Specifies the image size of the close button.
    * @default 100%
    */
  --ch-tab-close-button__background-image-size: 100%;
  display: grid;
  grid-template: "panel-container" 1fr/1fr;
}

:host(.ch-tab--block-start) {
  grid-template: "list-start list list-end" max-content "panel-container panel-container panel-container" 1fr/max-content 1fr max-content;
}

:host(.ch-tab--block-end) {
  grid-template: "panel-container panel-container panel-container" 1fr "list-start list list-end" max-content/max-content 1fr max-content;
}

:host(.ch-tab--inline-start) {
  grid-template: "list-start panel-container" max-content "list panel-container" 1fr "list-end panel-container" max-content/max-content 1fr;
}

:host(.ch-tab--inline-end) {
  grid-template: "panel-container list-start" max-content "panel-container list" 1fr "panel-container list-end" max-content/1fr max-content;
}

.tab-list {
  grid-area: list;
  display: grid;
}
.tab-list-start {
  grid-area: list-start;
}
.tab-list-end {
  grid-area: list-end;
}
.tab-list-start, .tab-list-end {
  display: flex;
  align-items: center;
}
.tab-list--inline {
  grid-auto-flow: row;
  grid-auto-rows: max-content;
}
.tab-list--block {
  grid-auto-flow: column;
  grid-auto-columns: max-content;
}

.panel-container {
  grid-area: panel-container;
  display: grid;
  grid-template: "selected" 1fr/1fr;
}

.panel {
  display: grid;
}
.panel--selected {
  grid-area: selected;
}

@supports (content-visibility: hidden) {
  .panel-container--collapsed,
  .panel--hidden {
    content-visibility: hidden;
  }
  .panel-container--collapsed {
    inline-size: 0;
  }
}
@supports not (content-visibility: hidden) {
  .panel-container--collapsed,
  .panel--hidden {
    display: none;
  }
}
.tab {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.tab:hover > .close-button::after, .tab:focus-within > .close-button::after, .tab[aria-selected=true] > .close-button::after {
  opacity: 1;
}
.tab-caption {
  contain: unset;
}

.no-captions {
  justify-content: center;
}

.sortable {
  -webkit-user-drag: element;
}

.tab {
  --ch-tab-button-start-img: var(--ch-start-img--base);
}
.tab.start-img--hover:hover {
  --ch-tab-button-start-img: var(--ch-start-img--hover);
}
.tab.start-img--active:active {
  --ch-tab-button-start-img: var(--ch-start-img--active);
}
.tab.start-img--focus:focus {
  --ch-tab-button-start-img: var(--ch-start-img--focus);
}
.tab.start-img--selected.selected {
  --ch-tab-button-start-img: var(--ch-start-img--selected);
}
.tab:disabled.start-img--disabled {
  --ch-tab-button-start-img: var(--ch-start-img--disabled);
}

.caption-image,
.pseudo-img--start::before {
  display: block;
  block-size: var(--ch-tab-button__image-size);
  inline-size: var(--ch-tab-button__image-size);
  min-inline-size: var(--ch-tab-button__image-size);
}

.pseudo-img--start::before {
  content: "";
  content-visibility: auto;
  contain-intrinsic-size: auto var(--ch-tab-button__image-size);
}

.start-img-type--background::before {
  background: no-repeat center/var(--ch-tab-button__background-image-size) var(--ch-tab-button-start-img);
}

.start-img-type--mask::before {
  -webkit-mask: no-repeat center/var(--ch-tab-button__background-image-size) var(--ch-tab-button-start-img);
  background-color: currentColor;
}

.close-button::after {
  content: "";
  display: flex;
  block-size: var(--ch-tab-close-button__image-size);
  inline-size: var(--ch-tab-close-button__image-size);
  -webkit-mask: no-repeat center/var(--ch-tab-close-button__background-image-size) url('data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"/></svg>');
  background-color: currentColor;
  opacity: 0;
}

.dragged-element {
  opacity: 0;
}
.dragged-element--outside {
  display: none;
}

.drag-preview {
  --ch-tab-snap-drag-preview: clamp(
    var(--ch-tab-list-start),
    var(--ch-tab-mouse-position),
    var(--ch-tab-list-end)
  );
  position: fixed;
  left: 0;
  top: 0;
  z-index: 108;
  cursor: grabbing;
}
.drag-preview:popover-open {
  padding: 0;
  margin: 0;
  border: unset;
  color: unset;
  inset: unset;
  left: 0;
  top: 0;
  pointer-events: none;
}
.drag-preview--outside-tab-list {
  transform: translate(calc(var(--ch-tab-mouse-position-x) - var(--ch-tab-mouse-offset-x)), calc(var(--ch-tab-mouse-position-y) - var(--ch-tab-mouse-offset-y)));
}
.drag-preview--inside-tab-list__block {
  --ch-tab-mouse-position: calc(
    var(--ch-tab-mouse-position-x) - var(--ch-tab-mouse-offset-x)
  );
  transform: translate(var(--ch-tab-snap-drag-preview), var(--ch-tab-button-position-y));
}
.drag-preview--inside-tab-list__inline {
  --ch-tab-mouse-position: calc(
    var(--ch-tab-mouse-position-y) - var(--ch-tab-mouse-offset-y)
  );
  transform: translate(var(--ch-tab-button-position-x), var(--ch-tab-snap-drag-preview));
}

.shifted-element {
  transition: transform var(--ch-tab-transition-duration);
}

:host(.ch-tab--block-start) .shifted-element--start,
:host(.ch-tab--block-end) .shifted-element--start {
  transform: translate(calc(var(--ch-tab-button-size) * -1), 0);
}
:host(.ch-tab--block-start) .shifted-element--end,
:host(.ch-tab--block-end) .shifted-element--end {
  transform: translate(var(--ch-tab-button-size), 0);
}

:host(.ch-tab--inline-start) .shifted-element--start,
:host(.ch-tab--inline-end) .shifted-element--start {
  transform: translateY(calc(var(--ch-tab-button-size) * -1));
}
:host(.ch-tab--inline-start) .shifted-element--end,
:host(.ch-tab--inline-end) .shifted-element--end {
  transform: translateY(var(--ch-tab-button-size));
}